import { useCallback, useState } from "react";
import type { PanelResizerState } from "../components/PanelResizer.tsx";
import { useUiPreference } from "./useUiPreference.ts";

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

interface Options {
  /** The `ui_preferences` key the width is saved under, per user. */
  preference: string;
  defaultWidth: number;
  min: number;
  max: number;
}

/**
 * A side panel's width: saved per user, live while its `PanelResizer` is
 * dragged, and held within `min`..`max` (a narrower window wins over the saved
 * width). Spread `resizer` into the `PanelResizer`.
 */
export function useResizableWidth({ preference, defaultWidth, min, max }: Options): {
  width: number;
  resizer: PanelResizerState;
} {
  const [savedWidth, setSavedWidth] = useUiPreference<number>(preference, defaultWidth, isNumber);
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const width = Math.min(max, Math.max(min, dragWidth ?? savedWidth));
  const onResizeEnd = useCallback(
    (next: number) => {
      setDragWidth(null);
      setSavedWidth(next);
    },
    [setSavedWidth],
  );
  const onReset = useCallback(() => onResizeEnd(defaultWidth), [onResizeEnd, defaultWidth]);
  return {
    width,
    resizer: { width, min, max, onResize: setDragWidth, onResizeEnd, onReset },
  };
}
